/**
 * @file Tutoriel guide : bulle de texte + halo autour de l'element [data-tutorial=...] de l'etape courante, bouton passer.
 */
import { TUTORIAL_STEPS } from '@config/index';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { currentScreen, send } from '../core/feedback';
import type { UiContext } from '../UiContext';

const PAD = 6;
const GAP = 12;

export class TutorialOverlay extends Component {
  private spot!: HTMLElement;
  private bubble!: HTMLElement;
  private text!: HTMLElement;
  private step!: HTMLElement;
  private lastRect = '';
  private lastStep = -2;
  private lastDone: boolean | null = null;
  private progress = 0;
  private lastProgress = -1;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'tutorial');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.spot = h('div', { class: 'spotlight is-hidden' });
    this.text = h('p', { class: 'tutorial-text' });
    this.step = h('span', { class: 'tutorial-step' });
    const skip = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'tutorial.skip' }) }, t.t('tutorial.skip'));
    this.bubble = h('div', { class: 'tutorial-bubble panel', 'data-ui-block': true }, this.step, this.text, skip);
    this.el.append(this.spot, this.bubble);
    this.listen('TutorialStep', (e) => {
      this.progress = e.progress ?? 0;
      this.ctx.sfx('pop');
    });
    this.update(0);
  }

  override update(_dt: number): void {
    const tut = this.ctx.sim.data.tutorial;
    const done = tut.done || tut.stepIndex >= TUTORIAL_STEPS.length;
    // Une modale ouverte (rapport hors-ligne, confirmation) passe avant le tutoriel.
    const hidden = done || (document.querySelector('.layer-modal')?.childElementCount ?? 0) > 0;
    if (hidden !== this.lastDone) {
      this.lastDone = hidden;
      toggleClass(this.el, 'is-hidden', hidden);
    }
    if (hidden) return;
    const step = TUTORIAL_STEPS[tut.stepIndex];
    if (!step) return;
    if (tut.stepIndex !== this.lastStep || this.progress !== this.lastProgress) {
      const newStep = tut.stepIndex !== this.lastStep;
      this.lastStep = tut.stepIndex;
      this.lastProgress = this.progress;
      setText(this.text, this.ctx.i18n.tDyn(step.textKey, { n: step.count ?? 1, done: this.progress }));
      setText(this.step, `${tut.stepIndex + 1}/${TUTORIAL_STEPS.length}`);
      this.lastRect = '';
      // Etape deja remplie (ecran Armes deja ouvert pendant l'etape precedente) : on la valide tout de suite au lieu de bloquer.
      if (newStep && step.completeOn === 'ScreenOpened' && currentScreen() === 'weapons') void this.ctx.sim.dispatch({ type: 'ui.screenOpened', screen: 'weapons' });
    }
    this.place(step.highlight);
  }

  /** Halo autour de l'element cible (mesure a chaque mise a jour : la mise en page est responsive) et bulle du bon cote. */
  private place(highlight: string): void {
    const host = this.el.getBoundingClientRect();
    const target = document.querySelector<HTMLElement>(`[data-tutorial="${highlight}"]`);
    const r = target && !target.classList.contains('is-hidden') ? target.getBoundingClientRect() : null;
    const usable = r !== null && r.width > 0 && r.height > 0;
    const key = usable ? `${r.left.toFixed(0)},${r.top.toFixed(0)},${r.width.toFixed(0)},${r.height.toFixed(0)},${host.height.toFixed(0)},${this.bubble.offsetHeight}` : `none,${host.height.toFixed(0)},${this.bubble.offsetHeight}`;
    if (key === this.lastRect) return;
    this.lastRect = key;
    toggleClass(this.spot, 'is-hidden', !usable);
    toggleClass(this.el, 'no-target', !usable);
    const bh = this.bubble.offsetHeight;
    let top: number;
    if (usable) {
      const left = r.left - host.left - PAD;
      const topSpot = r.top - host.top - PAD;
      this.spot.style.left = `${left.toFixed(1)}px`;
      this.spot.style.top = `${topSpot.toFixed(1)}px`;
      this.spot.style.width = `${(r.width + PAD * 2).toFixed(1)}px`;
      this.spot.style.height = `${(r.height + PAD * 2).toFixed(1)}px`;
      const below = topSpot + r.height + PAD * 2 + GAP;
      const above = topSpot - bh - GAP;
      // La bulle va du cote ou il y a le plus de place.
      top = topSpot + r.height / 2 < host.height / 2 ? below : above;
    } else {
      top = host.height * 0.3;
    }
    top = Math.max(GAP, Math.min(host.height - bh - GAP, top));
    this.bubble.style.transform = `translate3d(0, ${top.toFixed(1)}px, 0)`;
  }
}
